import { useEffect, useRef, type RefObject } from "react";

interface UseOnScrollIntoViewOptions {
  /** While true, the element is not watched. Default: false. */
  disabled?: boolean;
  /** How far outside the viewport still counts as in view, as a CSS margin. Default: none. */
  rootMargin?: string;
}

/**
 * Calls `onVisible` each time the element in `ref` scrolls into view (or within `rootMargin` of
 * it). The element is watched only while it is mounted and the hook is not disabled. `onVisible`
 * is stored in a ref, so callers do not need to memoize it.
 */
export function useOnScrollIntoView(
  ref: RefObject<Element | null>,
  onVisible: () => void,
  options: UseOnScrollIntoViewOptions = {}
): void {
  const { disabled = false, rootMargin } = options;
  const visible = useRef(onVisible);
  visible.current = onVisible;

  useEffect(() => {
    const node = ref.current;
    if (disabled || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) visible.current();
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, disabled, rootMargin]);
}
